import type { HoldingsDataProps, HoldingsGridRowData } from '..';
import { useHoldingsColumns } from './useHoldingsColumns';
import { useHoldings } from './useHoldings';
import { compact, isNil, orderBy, sumBy } from 'lodash';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { blockRowDisplayLimit, blockSettings, showBlockChangesColumn } from 'venn-state';
import { getChangesColumnDef } from './gridStyling';
import { holdingsColumnValueGetter } from './holdingsUtils';
import { RowDisplayLimitType } from 'venn-utils';

const useHoldingsDataGrid = ({ selectedRefId }: Pick<HoldingsDataProps, 'selectedRefId'>) => {
  const { data, categories } = useHoldings(selectedRefId);
  const showChangesColumn = useRecoilValue(showBlockChangesColumn(selectedRefId));
  const settings = useRecoilValue(blockSettings(selectedRefId));
  const blockType = settings.customBlockType;
  const rowDisplayLimit = useRecoilValue(blockRowDisplayLimit(selectedRefId));

  const { rowData, pinnedTopRowData } = useMemo((): {
    rowData: HoldingsGridRowData[];
    pinnedTopRowData: HoldingsGridRowData[] | undefined;
  } => {
    const subjectToAllocationMap = data?.breakdowns?.map((breakdown) =>
      Object.fromEntries(breakdown.holdingsContributions.map((bd) => [bd.id.id, bd.allocation])),
    );

    const rows = categories
      .map((category) => {
        const subjectToAllocation = subjectToAllocationMap?.map((map) => map[category.id.id] ?? 0) ?? [];
        const changes =
          subjectToAllocation?.length === 2 && !isNil(subjectToAllocation[1]) && !isNil(subjectToAllocation[0])
            ? subjectToAllocation[1] - subjectToAllocation[0]
            : 0;

        return {
          id: category.id,
          key: category.id.id,
          label: category.categoryName,
          type: 'PERCENTAGE' as const,
          value: subjectToAllocation,
          changes,
        };
      })
      .filter((row) => row.value?.some((alloc) => alloc));

    if (blockType === 'MUNI_BREAKDOWN') {
      const DISPLAY_THRESHOLD = 0.00001;
      const rowTotal = (row: HoldingsGridRowData) => sumBy(row.value, (v) => v ?? 0);

      const numColumns = rows[0]?.value.length ?? 0;
      const sortedRows = orderBy<HoldingsGridRowData>(
        rows.filter((row) => rowTotal(row) >= DISPLAY_THRESHOLD),
        Array.from({ length: numColumns }, (_, colIdx) => (row: HoldingsGridRowData) => row.value[colIdx] ?? 0),
        Array.from({ length: numColumns }, (): 'desc' => 'desc'),
      );

      const displayLimits: Partial<Record<RowDisplayLimitType, number>> = {
        [RowDisplayLimitType.TOP_5]: 5,
        [RowDisplayLimitType.TOP_10]: 10,
      };
      const limit = rowDisplayLimit ? displayLimits[rowDisplayLimit] : undefined;
      const filteredRows = limit ? sortedRows.slice(0, limit) : sortedRows;

      const totalValues = (filteredRows[0]?.value ?? []).map((_, colIdx) =>
        sumBy(filteredRows, (row) => row.value[colIdx] ?? 0),
      );

      const totalRow = {
        id: { id: 'muni-total' },
        key: 'muni-total',
        label: 'Total',
        type: 'PERCENTAGE' as const,
        value: totalValues,
        changes: 0,
        isTotal: true,
      };

      return { rowData: filteredRows, pinnedTopRowData: [totalRow] };
    }

    return { rowData: rows, pinnedTopRowData: undefined };
  }, [categories, data?.breakdowns, blockType, rowDisplayLimit]);

  const originalColumnDefs = useHoldingsColumns({
    selectedRefId,
    valueGetter: holdingsColumnValueGetter,
  });

  const columnDefs = useMemo(() => {
    if (!showChangesColumn) return originalColumnDefs;

    const [labelColumn, firstSubject, ...restSubjects] = originalColumnDefs;
    return compact([labelColumn, firstSubject, getChangesColumnDef(), ...restSubjects]);
  }, [originalColumnDefs, showChangesColumn]);

  return {
    rowData,
    columnDefs,
    pinnedTopRowData,
  };
};

export default useHoldingsDataGrid;
